import { useField } from '@framework/form/field/useField.tsx';
import { SelectInput } from '@framework/form/inputs/SelectInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { useCallback } from 'react';
import { useWatch } from 'react-hook-form';
import { useTreeReferencesContext } from '../../../../../tree/references/TreeReferencesContext.tsx';
import { useAsyncServiceEngineContext } from '../../../../expressions/repository/async/context/AsyncServicesEngineContext.tsx';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { StaleServiceOutputsAlert } from '../../common/async-widgets/StaleServiceOutputsAlert.tsx';
import { useAsyncServiceStaleOutputs } from '../../common/async-widgets/useAsyncServiceStaleOutputs.ts';
import { WidgetConfigurationSlotRender } from '../../common/config-slots/WidgetConfigurationSlotRender.tsx';
import { CommonConfigComponent } from '../common/CommonInputConfigComponent.tsx';
import { ServiceSettingsSlot } from '../common/ServiceSettingsSlot.tsx';
import { ServiceSelectorInput } from './components/ServiceSelectorInput.tsx';
import { AsynceSelectServiceTableConfigForm } from './components/service-config/AsynceSelectServiceTableConfigForm.tsx';
import { AsyncSelectServiceConfigForm } from './components/service-config/AsyncSelectServiceConfigForm.tsx';
import { useLabelFieldFixer } from './hooks/useLabelFieldFixer.ts';
import { type IAsyncSelectWidget } from './IAsyncSelectWidget.ts';

export interface IAsyncSelectConfigComponentProps
  extends IWidgetComponentProps<IAsyncSelectWidget> {}

export function AsyncSelectConfigComponent(
  props: IAsyncSelectConfigComponentProps,
) {
  const { references } = useTreeReferencesContext();
  const { services } = useAsyncServiceEngineContext();

  const selectedId = useWatch({ name: `serviceId` });
  const selected = services.find(s => s.id === selectedId);

  const { setValue: setLabelFields } = useField('labelFields');
  const { setValue: setValueField } = useField('valueField');
  const { setValue: setSearchField } = useField('searchField');
  const { setValue: setInputsField } = useField('inputs');
  const { setValue: setOutputsField } = useField('outputs');

  const searchField: string | null | undefined = useWatch({
    name: `searchField`,
  });

  const clearStaleSearchField = useCallback(() => {
    if (searchField && selected) {
      const inputExists = selected.inputs.some(i => i.name === searchField);
      if (!inputExists) {
        setSearchField(null);
      }
    }
  }, [searchField, selected, setSearchField]);

  const { diff, handleUpdate } = useAsyncServiceStaleOutputs({
    selected,
    setOutputsField,
    setLabelFields,
    setValueField,
    onAfterUpdate: clearStaleSearchField,
  });

  const hasInputs = selected?.inputs && selected?.inputs.length > 0;

  // Hook con useEffect()
  // Necessario per la retrocompatibilità.
  useLabelFieldFixer();

  return (
    <>
      <FormItem name="renderType" label="Tipo di visualizzazione">
        <SelectInput
          name="renderType"
          options={[
            { label: 'Menù a tendina', value: 'select' },
            { label: 'Tabella', value: 'table' },
          ]}
        />
      </FormItem>

      <CommonConfigComponent {...props} hideConfigDefaultValue />
      <WidgetConfigurationSlotRender slot={ServiceSettingsSlot}>
        <div>
          <ServiceSelectorInput
            onChange={(value, option, onChange) => {
              onChange(value);
              setLabelFields([]);
              setValueField(null);
              setSearchField(null);
              setInputsField({});
              setOutputsField((option as any).service.outputs);
            }}
            name="serviceId"
          />

          {diff?.isStale && (
            <StaleServiceOutputsAlert diff={diff} onUpdate={handleUpdate} />
          )}

          {/* Configurazione I/O servizio (modalità select) */}
          <AsyncSelectServiceConfigForm
            selected={selected}
            hasInputs={hasInputs}
            searchField={searchField}
            references={references}
          />
          {/* Configurazione I/O servizio (modalità tabella) */}
          <AsynceSelectServiceTableConfigForm
            selected={selected}
            hasInputs={hasInputs}
            references={references}
          />
        </div>
      </WidgetConfigurationSlotRender>
    </>
  );
}
